import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Users, BookOpen, Crown, Star } from 'lucide-react';
import { UnifiedHeader } from '../components/layout';
import { SEO } from '../components/seo/SEO';
import { PAGE_META } from '../config/seo';

const AboutUsPage: React.FC = () => {

  return (
    <div className="min-h-screen text-text-primary">
      <SEO title={PAGE_META['/about'].title} description={PAGE_META['/about'].description} path="/about" />
      <UnifiedHeader variant="heritage" currentPage="About Us" />
      
      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden mt-16">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-black/40 z-10"></div>
          <div 
            className="w-full h-full bg-cover transform scale-110"
            style={{
              backgroundImage: "url('/images/family-hero.jpeg')",
              backgroundPosition: '50% 80%',
              filter: 'blur(0.5px)'
            }}
          ></div>
        </div>
        
        <div className="relative z-20 text-center max-w-4xl px-4">
          <p className="text-white/80 text-sm font-medium mb-4">About Us</p>
          <h1 className="text-4xl md:text-6xl font-display font-semibold mb-6 text-white leading-tight tracking-wide" style={{ 
            textShadow: '2px 2px 6px rgba(0,0,0,0.8)'
          }}>
            Our Family Story
          </h1>
          <p className="text-lg text-white/90 leading-relaxed" style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.7)' }}>
            Discover our heritage, mission, and the dedicated people who guide our family association in preserving our legacy.
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 relative overflow-hidden">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-5xl md:text-6xl font-display font-bold mb-6 text-text-primary">
              Mission of the Association
            </h2>
            <p className="text-xl text-text-secondary max-w-3xl mx-auto">
              The purpose that drives our family association forward
            </p>
          </div>
          
          <div className="max-w-5xl mx-auto">
            <div className="bg-surface-1 rounded-lg p-12 border border-border shadow-sm text-center">
              <div className="w-20 h-20 bg-brand rounded-lg flex items-center justify-center mx-auto mb-8 shadow-sm">
                <Heart className="w-10 h-10 text-white" />
              </div>
              
              <h3 className="text-3xl font-bold mb-6 text-text-primary">
                Unity Through Heritage, Strengthened by Faith
              </h3>
              
              <p className="text-lg text-text-secondary leading-relaxed mb-8">
                The Redd Family Association exists to unite descendants of David Benjamin Redd and Patience Redd, 
                preserving our remarkable legacy while fostering spiritual growth and family bonds that span generations and continents. 
                We are committed to collecting and sharing our family's stories, supporting one another through life's journey, 
                and ensuring that the courage, faith, and resilience of our ancestors continues to inspire future generations.
              </p>
              
              <div className="bg-surface-2 rounded-lg p-6 border border-border">
                <p className="text-xl font-semibold text-text-primary italic font-display">
                  "A family that prays together, stays together"
                </p>
                <p className="text-text-secondary mt-2">
                  Our family motto that guides everything we do
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About The Redd Family Section */}
      <section className="py-20 px-4 bg-surface-1 relative">
        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-display font-bold mb-6 text-text-primary">
              About The Redd Family
            </h2>
            <p className="text-xl text-text-secondary max-w-3xl mx-auto">
              Discover the remarkable journey that began with courage and continues with faith
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div className="space-y-6">
              <p className="text-lg text-text-secondary leading-relaxed">
                The Redd Family Association traces its roots to David Benjamin Redd and Patience Redd, who courageously emigrated to Liberia in the 1800s as emancipated slaves. From their settlement in Bexley, Grand Bassa County, they raised eight children: Janie A. Redd-Davis, Ambrose Redd, Benjamin Redd, Sarah Redd-Cooke, Elizabeth Redd-Deshield, Elijah J. Redd, Amezette Redd-Brown, and Eliza Redd Moore-Watson.
              </p>
              <p className="text-lg text-text-secondary leading-relaxed">
                These eight children became the foundation of a family legacy spanning continents and centuries. Today, we unite descendants across the United States and beyond to preserve our remarkable history and strengthen the bonds that connect us all to David and Patience's courageous journey to freedom.
              </p>
            </div>
            <div className="text-center">
              <div className="relative overflow-hidden rounded-lg shadow-sm group border border-border">
                <img
                  src="/images/family-hero.jpeg"
                  alt="Redd Family Reunion Gathering"
                  className="w-full h-80 object-cover"
                />
                <div className="absolute inset-0 bg-black/20"></div>
              </div>
              <p className="text-lg text-text-secondary italic mt-6">
                Our family continues to gather, celebrating our shared heritage and bonds
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-display font-bold text-text-primary mb-4">Our Living Legacy</h2>
            <p className="text-xl text-text-secondary max-w-3xl mx-auto">
              From courage in Liberia to unity across continents - discover the remarkable journey of our family through generations
            </p>
          </div>
          
          <div className="relative">
            <div className="absolute left-4 md:left-1/2 h-full w-1 bg-brand transform md:-translate-x-1/2"></div>
            
            <div className="space-y-12">
              {[
                {
                  year: "1830s-1840s",
                  title: "The Journey to Freedom",
                  description: "David Benjamin and Patience Redd courageously emigrate to Bexley, Grand Bassa County, Liberia, establishing our family's foundation of faith and determination.",
                  image: "/images/timeline-roots.jpeg"
                },
                {
                  year: "1980s-1990s", 
                  title: "Return to America",
                  description: "Family descendants seek safety and opportunity in the United States, carrying our heritage across oceans while maintaining unbreakable bonds.",
                  image: "/images/timeline-growth.jpeg"
                },
                {
                  year: "2005",
                  title: "The Redd Family Association",
                  description: "At our Philadelphia Reunion, we formally establish our organization, cementing our commitment to unity, heritage, and future generations.",
                  image: "/images/timeline-heritage.png"
                },
                {
                  year: "2025",
                  title: "20 Years of Reunion",
                  description: "20 years after the Reunion was established, we gathered to celebrate our heritage once again and elected our youngest group of officers yet.",
                  image: "/images/family-hero.jpeg"
                }
              ].map((event, index) => (
                <div key={index} className={`relative flex items-center ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                  <div className={`w-full md:w-1/2 ${index % 2 === 0 ? 'md:pr-12' : 'md:pl-12'}`}>
                    <div className="bg-surface-1 rounded-lg shadow-sm overflow-hidden border border-border hover:shadow transition-all">
                      <img src={event.image} alt={event.title} className="w-full h-48 object-cover" />
                      <div className="p-8">
                        <div className="inline-block px-4 py-2 rounded-lg text-sm font-semibold mb-4 bg-surface-2 text-text-primary border border-border">
                          {event.year}
                        </div>
                        <h3 className="text-2xl font-display font-bold text-text-primary mb-4">{event.title}</h3>
                        <p className="text-text-secondary leading-relaxed">{event.description}</p>
                      </div>
                    </div>
                  </div>
                  <div className="absolute left-0 md:left-1/2 w-8 h-8 bg-brand rounded-full border-4 border-white shadow-sm transform md:-translate-x-1/2 flex items-center justify-center">
                    <div className="w-3 h-3 bg-white rounded-full"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-16 bg-surface-1 relative overflow-hidden">
        <div className="relative z-10 max-w-6xl mx-auto px-4">
          <div className="text-center mb-10">
            <div className="inline-flex items-center space-x-2 bg-surface-2 px-6 py-3 rounded-lg border border-border mb-8">
              <Star className="w-5 h-5 text-brand" />
              <span className="text-text-secondary text-sm font-medium">Our Core Values</span>
            </div>
            
            <h2 className="text-5xl md:text-7xl font-display font-bold mb-8 text-text-primary">
              Faith & Family Values
            </h2>
            
            <p className="text-xl text-text-secondary max-w-3xl mx-auto">
              The spiritual foundations and cherished principles that unite and strengthen our family bonds
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Heart,
                title: "Unity Through Heritage",
                description: "We believe in the power of shared ancestry to connect family members across generations, continents, and circumstances. From local reunions to international connections, we strengthen the bonds that unite all descendants of David and Patience Redd."
              },
              {
                icon: BookOpen,
                title: "Preservation of Legacy",
                description: "We are committed to collecting, maintaining, and sharing our family's stories, traditions, and artifacts. Through dedicated research and documentation, we ensure that the courage and resilience of our ancestors lives on for future generations."
              },
              {
                icon: Users,
                title: "Education and Empowerment", 
                description: "We foster learning about our rich heritage while developing resources that engage and inspire younger generations. By understanding where we came from, we empower family members to carry our values forward with pride and purpose."
              },
              {
                icon: Star,
                title: "Mutual Support",
                description: "We stand together in times of celebration and challenge, providing assistance and encouragement to family members in need. This spirit of caring, passed down from David and Patience, defines who we are as a family."
              }
            ].map((value, index) => (
              <div key={index} className="group relative">
                <div className="bg-surface-1 rounded-lg p-8 border border-border transition-all hover:shadow-sm min-h-[300px] relative overflow-hidden">
                  <div className="w-16 h-16 bg-brand rounded-lg flex items-center justify-center mb-6 shadow-sm">
                    <value.icon className="w-8 h-8 text-white" />
                  </div>
                  
                  <h3 className="text-xl font-bold mb-4 text-text-primary">
                    {value.title}
                  </h3>
                  
                  <p className="text-text-secondary leading-relaxed">
                    {value.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Meet the Officers CTA */}
      <section className="py-16 relative overflow-hidden">
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <div className="inline-flex items-center space-x-2 bg-surface-1 px-6 py-3 rounded-lg border border-border mb-8">
            <Crown className="w-5 h-5 text-brand" />
            <span className="text-text-secondary text-sm font-medium">Our Leadership Team</span>
          </div>

          <h2 className="text-5xl md:text-7xl font-display font-bold mb-8 text-text-primary">
            Meet the Officers
          </h2>

          <p className="text-xl text-text-secondary max-w-3xl mx-auto mb-10">
            Dedicated leaders who guide our family association with wisdom, integrity, and unwavering commitment to our heritage
          </p>

          <Link
            to="/officers"
            className="bg-brand hover:bg-brand-dark text-white px-10 py-4 rounded-lg text-lg font-semibold shadow-sm transition-all inline-flex items-center space-x-3"
          >
            <Crown className="w-5 h-5" />
            <span>Meet the Officers</span>
          </Link>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-surface-1 relative overflow-hidden">
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <div className="bg-surface-2 rounded-lg p-12 border border-border shadow-sm">
            <div className="w-20 h-20 bg-brand rounded-lg flex items-center justify-center mx-auto mb-8 shadow-sm">
              <Heart className="w-10 h-10 text-white" />
            </div>
            
            <h2 className="text-4xl font-display font-bold mb-6 text-text-primary">
              Join Our Family Portal
            </h2>
            
            <p className="text-lg text-text-secondary mb-8 leading-relaxed max-w-2xl mx-auto">
              Access exclusive family content, reunion details, payment management, family directory, and special announcements for verified family members.
            </p>
            
            <button className="bg-brand hover:bg-brand-dark text-white px-12 py-4 rounded-lg text-xl font-semibold shadow-sm transition-all inline-flex items-center space-x-3">
              <Users className="w-6 h-6" />
              <span>Access Family Portal</span>
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUsPage;
